// Player profile (spec §3.1, P20): name, alliance and rank, shield, owned systems, points (G-78).
import { Shield } from 'lucide-react';
import { Link, useOutletContext, useParams } from 'react-router';
import { ApiRequestError } from '../api/http';
import { usePublicPlayer } from '../api/queries';
import { RANK_LABELS } from '../components/alliance/shared';
import type { GameContext } from '../components/GameLayout';
import { AllianceTagLink } from '../components/ProfileLinks';
import { EmptyState, ErrorNotice, Page, Panel, Spinner, Stat } from '../components/ui';

export function PlayerScreen() {
  const { galaxyId } = useOutletContext<GameContext>();
  const { playerId } = useParams();
  const player = usePublicPlayer(galaxyId, playerId);
  if (player.error) {
    return (
      <Page width="narrow">
        {player.error instanceof ApiRequestError && player.error.status === 404 ? (
          <EmptyState>Player not found.</EmptyState>
        ) : (
          <ErrorNotice error={player.error} />
        )}
      </Page>
    );
  }
  if (!player.data) return <Spinner />;
  const p = player.data;
  return (
    <Page width="narrow">
      <header className="space-y-1" data-player-profile>
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl font-bold uppercase tracking-[0.08em] text-text">
            {p.displayName}
          </h1>
          {p.shielded ? <Shield className="size-5 text-info" aria-label="Beginner shield" /> : null}
        </div>
        {p.alliance ? (
          <p className="text-sm text-muted">
            <AllianceTagLink galaxyId={galaxyId} allianceId={p.alliance.id} tag={p.alliance.tag} />{' '}
            · {RANK_LABELS[p.alliance.rank]}
          </p>
        ) : (
          <p className="text-sm text-muted">No alliance</p>
        )}
      </header>
      <Panel>
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Systems" value={p.stats.systemsOwned} />
          <Stat label="Points" value={<span data-points>{p.stats.points}</span>} />
        </div>
      </Panel>
      <Panel title="Systems">
        <ul className="space-y-2">
          {p.systems.map((s) => (
            <li key={s.id}>
              <Link
                to={`/g/${galaxyId}/systems/${s.id}`}
                className="block hud-cut-sm border border-line bg-void/60 p-3 hover:border-info"
              >
                <span className="text-sm text-text">{s.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      </Panel>
    </Page>
  );
}
